import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import Pagination from './components/Pagination';
import Verse from './components/Verse';
import Controls from './components/Controls';
import SearchModal from './components/SearchModal';
import { getEndingPatternStyle, getFirstUniqueSequenceStyle, getFirstWordStyle, getPositionColor, getRootColor } from './utils/visualizations';

const apiCache = {};

function App() {
  const [currentPage, setCurrentPage] = useState(() => {
    return localStorage.getItem('page') || 1;
  });


  const [ayahs, setAyahs] = useState([]);
  const [data, setData] = useState({});
  const [isPlayingAll, setIsPlayingAll] = useState(false);
  const [currentVerseIndex, setCurrentVerseIndex] = useState(-1);
  const [repeatAllCount, setRepeatAllCount] = useState(0);
  const currentRepeatAllRef = useRef(0); // New ref
  const [currentVerseRepeat, setCurrentVerseRepeat] = useState(0);
  const [hiddenWords, setHiddenWords] = useState({});
  const [hiddenVerses, setHiddenVerses] = useState({});
  const [addSilence, setAddSilence] = useState(() => localStorage.getItem('addSilence') === 'true');
  const [currentFont, setCurrentFont] = useState(() => localStorage.getItem('currentFont') || 'font-uthmani');
  const [isLoading, setIsLoading] = useState(false);
  const [metaData, setMetaData] = useState(null);
  const [paginationType, setPaginationType] = useState(() => localStorage.getItem('PaginationType') || 'page');
  const [displayMode, setDisplayMode] = useState(() => localStorage.getItem('displayMode') || localStorage.getItem('PaginationType') || 'page');
  const [currentFavoriteId, setCurrentFavoriteId] = useState(() => parseInt(localStorage.getItem('currentFavoriteId')) || null);
  const [autoPaginate, setAutoPaginate] = useState(() => localStorage.getItem('autoPaginate') === 'true');
  const [coloringModes, setColoringModes] = useState(() => localStorage.getItem('ColoringModes') || '');
  const [fontSizeFactor, setFontSizeFactor] = useState(() => parseFloat(localStorage.getItem('fontSizeFactor')) || 1);
  const [textOpacity, setTextOpacity] = useState(() => parseFloat(localStorage.getItem('textOpacity')) || 1);
  const [isPaused, setIsPaused] = useState(false);

  const [playMode, setPlayMode] = useState(() => localStorage.getItem('playMode') || 'all');
  const [startAyah, setStartAyah] = useState(null);
  const [endAyah, setEndAyah] = useState(null);

  const [repeatAll, setRepeatAll] = useState(() => parseInt(localStorage.getItem('repeatAll')) || 1);
  const [repeatVerse, setRepeatVerse] = useState(() => parseInt(localStorage.getItem('repeatVerse')) || 1);

  const [displayStartAyah, setDisplayStartAyah] = useState(null);
  const [displayEndAyah, setDisplayEndAyah] = useState(null);

  // Add these refs to track current settings
  const currentSettings = useRef({
    addSilence: false,
    repeatVerse: 1,
    repeatAll: 1,
    playMode: 'all',
    startAyah: null,
    endAyah: null,
  });


  // console.log(ayahs);

  const [selectedEdition, setSelectedEdition] = useState(() => localStorage.getItem('audioEdition') || 'ar.alafasy');
  const [totalPages, setTotalPages] = useState(604);
  const currentAudio = useRef(null);
  const shouldAutoPlayNext = useRef(false);
  const playTimeoutRef = useRef(null);
  const lastScrolledConfig = useRef(null);

  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [scrollToAyah, setScrollToAyah] = useState(null);

  useEffect(() => { localStorage.setItem('addSilence', addSilence.toString()); }, [addSilence]);
  useEffect(() => { localStorage.setItem('currentFont', currentFont); }, [currentFont]);
  useEffect(() => { localStorage.setItem('playMode', playMode); }, [playMode]);
  useEffect(() => { localStorage.setItem('repeatAll', repeatAll.toString()); }, [repeatAll]);
  useEffect(() => { localStorage.setItem('repeatVerse', repeatVerse.toString()); }, [repeatVerse]);
  useEffect(() => { localStorage.setItem('autoPaginate', autoPaginate.toString()); }, [autoPaginate]);
  useEffect(() => { localStorage.setItem('displayMode', displayMode); }, [displayMode]);
  useEffect(() => {
    if (currentFavoriteId !== null) localStorage.setItem('currentFavoriteId', currentFavoriteId.toString());
  }, [currentFavoriteId]);

  useEffect(() => { localStorage.setItem('fontSizeFactor', fontSizeFactor.toString()); }, [fontSizeFactor]);
  useEffect(() => { localStorage.setItem('textOpacity', textOpacity.toString()); }, [textOpacity]);

  const loadFavoriteById = (favId) => {
    // If we're an automated play loop, shouldAutoPlayNext is true, otherwise we should stop audio immediately
    if (!shouldAutoPlayNext.current) {
      resetPlayback();
    }
    const favs = JSON.parse(localStorage.getItem('quran_favorites') || '[]');
    const fav = favs.find(f => f.id === favId);
    if (fav) {
      setCurrentFavoriteId(fav.id);
      setPaginationType(fav.paginationType);
      localStorage.setItem('PaginationType', fav.paginationType);
      setCurrentPage(fav.currentPage);
      localStorage.setItem('page', fav.currentPage);
      if (fav.paginationType === 'surah') {
        setDisplayStartAyah(fav.displayStartAyah);
        setDisplayEndAyah(fav.displayEndAyah);
      } else {
        setDisplayStartAyah(null);
        setDisplayEndAyah(null);
      }
    }
  };

  useEffect(() => {
    if (displayMode === 'favorite') {
      const favs = JSON.parse(localStorage.getItem('quran_favorites') || '[]');
      if (favs.length > 0) {
        const exists = favs.find(f => f.id === currentFavoriteId);
        if (!exists) loadFavoriteById(favs[0].id);
        else loadFavoriteById(currentFavoriteId);
      }
    }
  }, [displayMode]);

  useEffect(() => {
    loadPage();
  }, [currentPage, paginationType, selectedEdition]);
  // Update the ref whenever settings change
  useEffect(() => {
    currentSettings.current = {
      addSilence,
      repeatVerse,
      repeatAll,
      playMode,
      startAyah,
      endAyah,
    };
  }, [addSilence, repeatVerse, repeatAll, playMode, startAyah, endAyah]);

  useEffect(() => {
    if (ayahs.length > 0) {
      setStartAyah(ayahs[0].numberInSurah);
      setEndAyah(ayahs[ayahs.length - 1].numberInSurah);
    }
  }, [ayahs]);

  // removed unused frequencyMap useEffect

  useEffect(() => {
    async function fetchMeta() {
      try {
        const response = await fetch('https://api.alquran.cloud/v1/meta');
        const d = await response.json();
        setMetaData(d.data);
      } catch (error) {
        console.error('Error fetching meta:', error);
      }
    }
    fetchMeta();
  }, []);

  useEffect(() => {
    if (shouldAutoPlayNext.current && ayahs.length > 0 && !isLoading) {
      // Small pause before playback begins for smooth transition
      const timer = setTimeout(() => {
        shouldAutoPlayNext.current = false;
        if (!isPlayingAll) {
          togglePlayAll();
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [ayahs, isLoading, isPlayingAll]);

  useEffect(() => {
    if (isLoading || ayahs.length === 0) return;

    const currentConfig = `${currentPage}-${paginationType}-${displayStartAyah}-${displayEndAyah}`;

    if (scrollToAyah) {
      lastScrolledConfig.current = currentConfig;
      const timer = setTimeout(() => {
        const targetElement = document.getElementById(`ayah-${scrollToAyah}`);
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          targetElement.classList.add('highlight-scroll');
          setTimeout(() => targetElement.classList.remove('highlight-scroll'), 3000);
        }
        setScrollToAyah(null);
      }, 500);
      return () => clearTimeout(timer);
    } else if (lastScrolledConfig.current !== currentConfig) {
      lastScrolledConfig.current = currentConfig;
      const timer = setTimeout(() => {
        const firstDisplayedAyahNumber = ayahs.find(a => 
            (!displayStartAyah || a.numberInSurah >= displayStartAyah) && 
            (!displayEndAyah || a.numberInSurah <= displayEndAyah)
        )?.numberInSurah;
        
        if (firstDisplayedAyahNumber) {
            const targetElement = document.getElementById(`ayah-${firstDisplayedAyahNumber}`);
            if (targetElement) {
                targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        } else {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [ayahs, isLoading, scrollToAyah, displayStartAyah, displayEndAyah, currentPage, paginationType]);

  useEffect(() => {
    return () => {
      if (currentAudio.current) {
        currentAudio.current.pause();
      }
    };
  }, []);

  async function loadPage() {
    try {
      const max = paginationType === "surah" && currentPage > 114 ? 1 : currentPage;
      const cacheKey = `${paginationType}-${max}-${selectedEdition}`;

      setIsLoading(true);

      if (apiCache[cacheKey]) {
        const data = apiCache[cacheKey];
        setAyahs(data.ayahs);
        setData(data);
        setHiddenWords({});
        setHiddenVerses({});
        resetPlayback();
        setIsLoading(false);
        return;
      }

      const response = await fetch(`https://api.alquran.cloud/v1/${paginationType}/${max}/${selectedEdition}`);
      const data = await response.json();

      apiCache[cacheKey] = data.data;

      setAyahs(data.data.ayahs);
      setData(data.data);
      setHiddenWords({});
      setHiddenVerses({});
      resetPlayback();

    } catch (error) {
      console.error('Error loading page:', error);
    } finally {
      setIsLoading(false);
    }
  }

  function splitVerseIntoWords(verse, verseIndex, surahNumber) {


    // Remove Basmalah if it's the first verse and not Surah Al-Fatihah
    let v = verse;
    const isBasmalah = verse.includes("بِسْمِ ٱللَّهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ");
    if (verseIndex === 0 && surahNumber !== 1 && isBasmalah) {
      v = v.replace("بِسْمِ ٱللَّهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ", "").trim();
    }

    const words = v.split(' ').filter(word => word.length > 0 && word !== "۞"); // Exclude decoration

    return words.map((word, wordIndex) => {
      const classNames = ['word'];
      const styles = {};


      // 1. Word Position Coloring
      if (coloringModes === "wordPosition") {
        styles.backgroundColor = getPositionColor(word, wordIndex);
      }

      // 2. Ending Patterns
      if (coloringModes === "endingPatterns") {
        Object.assign(styles, getEndingPatternStyle(word, words));
      }

      // 3. First Word Coloring
      if (coloringModes === "firstWordColoring") {
        Object.assign(styles, getFirstUniqueSequenceStyle(wordIndex, verseIndex, ayahs));
      }
      if (coloringModes === "firstUniqueWordSetColoring") {
        Object.assign(styles, getFirstUniqueSequenceStyle(wordIndex, verseIndex, ayahs));
      }
      // 3. Root Words
      // if (visualizationSettings.rootWords) {
      //   const rootColor = getRootColor(word);
      //   if (rootColor) {
      //     styles.color = rootColor;
      //     tooltip += `Root word detected\n`;
      //   }
      // }

      // 4. Verse Structure


      // 5. Tajweed Rules




      // Calculate the adjusted word index considering Basmalah removal
      const adjustedWordIndex = isBasmalah && verseIndex === 0 && surahNumber !== 1 ?
        wordIndex + 4 : wordIndex;

      // A word is hidden if:
      // 1. It's explicitly hidden in hiddenWords, OR
      // 2. Its entire verse is hidden in hiddenVerses, AND
      // 3. It's not a single character (like waaw or other connecting letters)
      const isHidden =
        hiddenWords[`${verseIndex}-${adjustedWordIndex}`]
        //  ||
        // hiddenVerses[verseIndex]
        && word.length > 1;



      return (
        <span
          key={`${verseIndex}-${adjustedWordIndex}`}
          className={`word ${isHidden ? 'hidden-word' : ''}`}

          data-verse={verseIndex}
          data-word={adjustedWordIndex}
          onClick={() => toggleWordVisibility(verseIndex, adjustedWordIndex)}
          style={Object.keys(styles).length > 0 ? styles : null}
        >
          {word}
        </span>
      );
    }).reduce((prev, curr) => [prev, ' ', curr], []);
  }

  function toggleWordVisibility(verseIndex, wordIndex) {
    setHiddenWords(prev => {
      const key = `${verseIndex}-${wordIndex}`;
      const newState = { ...prev };
      if (newState[key]) {
        delete newState[key];
      }
      return newState;
    });
  }

  function toggleVerseVisibility(verseIndex) {
    setHiddenVerses(prev => {
      const newHiddenVerses = { ...prev };

      if (newHiddenVerses[verseIndex]) {
        // Showing the verse - remove from hidden verses
        delete newHiddenVerses[verseIndex];

        // Also show all words in this verse
        setHiddenWords(prevWords => {
          const newHiddenWords = { ...prevWords };
          const words = ayahs[verseIndex]?.text.split(' ') || [];

          words.forEach((_, wordIndex) => {
            const wordKey = `${verseIndex}-${wordIndex}`;
            delete newHiddenWords[wordKey];
          });

          return newHiddenWords;
        });
      } else {
        // Hiding the verse - add to hidden verses
        newHiddenVerses[verseIndex] = true;

        // Also hide all words in this verse
        setHiddenWords(prevWords => {
          const newHiddenWords = { ...prevWords };
          const words = ayahs[verseIndex]?.text.split(' ') || [];

          words.forEach((_, wordIndex) => {
            const wordKey = `${verseIndex}-${wordIndex}`;
            newHiddenWords[wordKey] = true;
          });

          return newHiddenWords;
        });
      }

      return newHiddenVerses;
    });
  }

  function togglePlayVerse(index) {
    const wasPlayingThis = currentAudio.current && currentVerseIndex === index && !currentAudio.current.paused;
    
    if (wasPlayingThis) {
      resetPlayback();
      return;
    }

    if (isPlayingAll) {
      // Jump global playback to this verse
      stopCurrentAudio();
      setIsPaused(false);
      // Reset repeat counters for the new starting point in global playback
      currentRepeatAllRef.current = 0; 
      setCurrentVerseRepeat(0);
      playVerse(index, true, 0);
    } else {
      // Regular single verse play
      resetPlayback();
      playVerse(index);
    }
  }

  function playVerse(index, isPartOfAll = false, verseRepeatCount = 0) {
    if (!ayahs[index]) {
      resetPlayback();
      return;
    }

    stopCurrentAudio();

    setCurrentVerseIndex(index);
    setIsPaused(false);

    // Safely extract audio URL
    const audioUrl = ayahs[index].audio || '';
    if (!audioUrl) {
      console.error('No audio URL found for this verse');
      resetPlayback();
      return;
    }

    if (!currentAudio.current) {
      currentAudio.current = new Audio();
    }

    currentAudio.current.src = audioUrl;
    currentAudio.current.load();

    currentAudio.current.onended = () => {
      const { addSilence, repeatVerse, repeatAll, playMode, startAyah, endAyah } = currentSettings.current;

      const shouldRepeatVerse = verseRepeatCount < repeatVerse - 1;
      let shouldGoToNextVerse = false;
      let nextIndex = index + 1;

      if (paginationType === 'surah' && (displayStartAyah || displayEndAyah)) {
        const nextAyahObj = ayahs[nextIndex];
        const isOutOfRange = nextAyahObj && (
          (displayStartAyah && nextAyahObj.numberInSurah < displayStartAyah) ||
          (displayEndAyah && nextAyahObj.numberInSurah > displayEndAyah)
        );
        shouldGoToNextVerse = nextIndex < ayahs.length && !isOutOfRange;
      } else if (playMode === 'range' && startAyah && endAyah) {
        const endIndex = ayahs.findIndex(ayah => ayah.numberInSurah === endAyah);
        shouldGoToNextVerse = nextIndex <= endIndex && nextIndex < ayahs.length;
      } else {
        shouldGoToNextVerse = nextIndex < ayahs.length;
      }

      const shouldRepeatAll = isPartOfAll && !shouldGoToNextVerse && currentRepeatAllRef.current < repeatAll - 1;

      console.log({
        index,
        verseRepeatCount,
        currentRepeatAll: currentRepeatAllRef.current,
        repeatAll,
        shouldRepeatVerse,
        shouldGoToNextVerse,
        shouldRepeatAll,
      });

      if (isPartOfAll && shouldRepeatVerse) {
        setCurrentVerseRepeat(prev => prev + 1);
        schedulePlay(index, true, verseRepeatCount + 1);
      } else if (isPartOfAll && shouldGoToNextVerse) {
        setCurrentVerseRepeat(0);
        schedulePlay(nextIndex, true, 0);
      } else if (shouldRepeatAll) {
        currentRepeatAllRef.current += 1;
        console.log('Incrementing currentRepeatAll to:', currentRepeatAllRef.current);
        setCurrentVerseRepeat(0);
        let startIndex = 0;
        if (paginationType === 'surah' && displayStartAyah) {
          const foundIndex = ayahs.findIndex(ayah => ayah.numberInSurah === displayStartAyah);
          startIndex = foundIndex >= 0 ? foundIndex : 0;
        } else if (playMode === 'range' && startAyah) {
          const foundIndex = ayahs.findIndex(ayah => ayah.numberInSurah === startAyah);
          startIndex = foundIndex >= 0 ? foundIndex : 0;
        }

        if (startIndex >= 0 && startIndex < ayahs.length) {
          schedulePlay(startIndex, true, 0);
        } else {
          resetPlayback();
        }
      } else if (autoPaginate) {
        if (displayMode === 'favorite') {
          const favs = JSON.parse(localStorage.getItem('quran_favorites') || '[]');
          const idx = favs.findIndex(f => f.id === currentFavoriteId);
          if (idx >= 0 && idx + 1 < favs.length) {
            shouldAutoPlayNext.current = true;
            loadFavoriteById(favs[idx + 1].id);
          } else if (favs.length > 0) {
            shouldAutoPlayNext.current = true;
            loadFavoriteById(favs[0].id); // loop back
          } else {
            resetPlayback();
          }
        } else {
          if (currentPage < totalPages) {
            shouldAutoPlayNext.current = true;
            changePage(currentPage + 1);
          } else {
            shouldAutoPlayNext.current = true;
            changePage(1); // loop back
          }
        }
      } else {
        resetPlayback();
      }
    };

    currentAudio.current.play().catch(err => {
      console.error('Playback error:', err);
      resetPlayback();
    });
  }
  // Helper function to schedule playback with optional delay
  // Helper function to schedule playback with optional delay
  function schedulePlay(index, isPartOfAll, verseRepeatCount) {
    if (playTimeoutRef.current) {
      clearTimeout(playTimeoutRef.current);
      playTimeoutRef.current = null;
    }
    const { addSilence } = currentSettings.current;
    const delay = addSilence
      ? currentAudio.current.duration * 1000
      : 0;

    if (delay > 0) {
      playTimeoutRef.current = setTimeout(() => {
        playVerse(index, isPartOfAll, verseRepeatCount);
      }, delay);
    } else {
      playVerse(index, isPartOfAll, verseRepeatCount);
    }
  }

  function togglePause() {
    if (currentAudio.current) {
      if (isPaused) {
        currentAudio.current.play().catch(err => console.error('Error resuming:', err));
        setIsPaused(false);
      } else {
        currentAudio.current.pause();
        setIsPaused(true);
      }
    }
  }

  // Update togglePlayAll to use current settings
  function togglePlayAll() {
    if (isPlayingAll) {
      stopCurrentAudio();
      resetPlayback();
    } else {
      setIsPlayingAll(true);
      setIsPaused(false);
      currentRepeatAllRef.current = 0; // Reset ref
      setCurrentVerseRepeat(0);

      let startIndex = 0;
      if (paginationType === 'surah' && displayStartAyah) {
        const foundIndex = ayahs.findIndex(ayah => ayah.numberInSurah === displayStartAyah);
        startIndex = foundIndex >= 0 ? foundIndex : 0;
      } else if (playMode === 'range' && startAyah) {
        const foundIndex = ayahs.findIndex(ayah => ayah.numberInSurah === startAyah);
        startIndex = foundIndex >= 0 ? foundIndex : 0;
      }

      if (startIndex >= 0 && startIndex < ayahs.length) {
        playVerse(startIndex, true, 0);
      } else {
        resetPlayback();
      }
    }
  }
  function stopCurrentAudio() {
    if (playTimeoutRef.current) {
      clearTimeout(playTimeoutRef.current);
      playTimeoutRef.current = null;
    }
    if (currentAudio.current) {
      currentAudio.current.pause();
      // Keep object alive instead of nullifying to prevent autoplay DOM issues
    }
    setCurrentVerseIndex(-1);
  }

  function resetPlayback() {
    stopCurrentAudio();
    setIsPlayingAll(false);
    setIsPaused(false);
    setCurrentVerseIndex(-1);
    currentRepeatAllRef.current = 0; // Reset ref
    setCurrentVerseRepeat(0);
  }

  function showAll() {
    setHiddenWords({});
    setHiddenVerses({});
  }

  function hideAll() {
    const newHiddenVerses = {};
    const newHiddenWords = {};

    ayahs.forEach((ayah, verseIndex) => {
      newHiddenVerses[verseIndex] = true;
      const words = ayah.text.split(' ');

      words.forEach((_, wordIndex) => {
        newHiddenWords[`${verseIndex}-${wordIndex}`] = true;
      });
    });

    setHiddenVerses(newHiddenVerses);
    setHiddenWords(newHiddenWords);
  }

  function hideRandomVerses(n = 0.5) {
    showAll();
    const newHiddenVerses = {};
    const newHiddenWords = {};

    ayahs.forEach((ayah, verseIndex) => {
      if (Math.random() < n) {
        newHiddenVerses[verseIndex] = true;
        const words = ayah.text.split(' ');

        words.forEach((_, wordIndex) => {
          newHiddenWords[`${verseIndex}-${wordIndex}`] = true;
        });
      }
    });

    setHiddenVerses(newHiddenVerses);
    setHiddenWords(newHiddenWords);
  }

  function hideRandomWords(n = 0.5) {
    showAll();
    const newHiddenWords = {};
    
    // Determine maximum successive hidden words based on difficulty: (difficulty % / 20)
    let maxSuccessive = Math.max(1, Math.round((n * 100) / 20));
    
    let successiveHidden = 0;

    ayahs.forEach((_, verseIndex) => {
      const words = ayahs[verseIndex].text.split(' ');
      words.forEach((_, wordIndex) => {
        if (successiveHidden >= maxSuccessive) {
          successiveHidden = 0; // Force visibility
          return;
        }

        if (Math.random() < n) {
          newHiddenWords[`${verseIndex}-${wordIndex}`] = true;
          successiveHidden++;
        } else {
          successiveHidden = 0;
        }
      });
    });
    setHiddenWords(newHiddenWords);
  }

  const changePaginationType = (pt) => {
    resetPlayback();
    setCurrentPage(1)
    if (pt === "page") {
      setTotalPages(604)
    } else {
      setTotalPages(114)
    }
    setPaginationType(pt);
    setDisplayStartAyah(null);
    setDisplayEndAyah(null);
    localStorage.setItem('PaginationType', pt);
    localStorage.setItem('page', 1);
  }

  function changePage(newPage) {
    // const newPage = parseInt(currentPage) + delta;
    if (newPage >= 1 && newPage <= totalPages) {
      if (!shouldAutoPlayNext.current) {
        resetPlayback();
      }
      setCurrentPage(newPage);
      localStorage.setItem('page', newPage);
    }
  }

  const HandleColoring = (Col_Mode) => {
    localStorage.setItem('ColoringModes', Col_Mode);
    setColoringModes(Col_Mode)
  }

  const handleSearchResultSelect = (surahNumber, ayahNumber) => {
    resetPlayback();
    setIsSearchOpen(false);

    // Switch to Surah mode and load that exact surah
    setPaginationType('surah');
    setDisplayMode('surah');
    setCurrentPage(surahNumber);
    setDisplayStartAyah(null);
    setDisplayEndAyah(null);
    setCurrentFavoriteId(null);

    localStorage.setItem('PaginationType', 'surah');
    localStorage.setItem('displayMode', 'surah');
    localStorage.setItem('page', surahNumber);
    localStorage.removeItem('currentFavoriteId');

    // Store the ayah we want to scroll to once loaded
    setScrollToAyah(ayahNumber);
  };

  let currentSurahNumber = null;

  return (
    <div className={`quran-app`}>
      <SearchModal
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        onSelectResult={handleSearchResultSelect}
      />

      <Controls
        ayahs={ayahs} playMode={playMode} setPlayMode={setPlayMode}
        startAyah={startAyah} setStartAyah={setStartAyah}
        endAyah={endAyah} setEndAyah={setEndAyah}
        coloringModes={coloringModes} setColoringModes={HandleColoring}
        selectedEdition={selectedEdition} setSelectedEdition={setSelectedEdition}
        showAll={showAll} hideAll={hideAll}
        hideRandomWords={hideRandomWords} hideRandomVerses={hideRandomVerses}
        currentPage={currentPage} togglePlayAll={togglePlayAll}
        isPlayingAll={isPlayingAll} repeatAll={repeatAll} setRepeatAll={setRepeatAll}
        repeatVerse={repeatVerse} setRepeatVerse={setRepeatVerse}
        addSilence={addSilence} setAddSilence={setAddSilence}
        changePage={changePage} changePaginationType={changePaginationType}
        paginationType={paginationType} setCurrentFont={setCurrentFont}
        setCurrentPage={changePage} totalPages={totalPages}
        displayStartAyah={displayStartAyah} setDisplayStartAyah={setDisplayStartAyah}
        displayEndAyah={displayEndAyah} setDisplayEndAyah={setDisplayEndAyah}
        metaData={metaData}
        autoPaginate={autoPaginate} setAutoPaginate={setAutoPaginate}
        displayMode={displayMode} setDisplayMode={setDisplayMode}
        currentFavoriteId={currentFavoriteId} setCurrentFavoriteId={setCurrentFavoriteId}
        loadFavoriteById={loadFavoriteById}
        setIsSearchOpen={setIsSearchOpen}
        fontSizeFactor={fontSizeFactor} setFontSizeFactor={setFontSizeFactor}
        textOpacity={textOpacity} setTextOpacity={setTextOpacity}
        isPaused={isPaused} togglePause={togglePause}
      />

      <div className="container" style={{ '--size-factor': fontSizeFactor, '--text-opacity': textOpacity }}>
        <div id="quranContent" className={`quranContent ${currentFont}`}>
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '100px', fontSize: '24px', color: '#666' }}>
              جارٍ التحميل...
            </div>
          ) : (
            ayahs.map((ayah, index) => {
              const isOutOfRange = paginationType === "surah" &&
                ((displayStartAyah && ayah.numberInSurah < displayStartAyah) ||
                  (displayEndAyah && ayah.numberInSurah > displayEndAyah));
              if (isOutOfRange) return null;

              const firstDisplayedAyahNumber = ayahs.find(a =>
                (!displayStartAyah || a.numberInSurah >= displayStartAyah) &&
                (!displayEndAyah || a.numberInSurah <= displayEndAyah)
              )?.numberInSurah;

              const showSurahHeader = paginationType === "page" ? currentSurahNumber !== ayah.surah?.number : ayah.numberInSurah === (firstDisplayedAyahNumber || 1);
              let surahName = "";
              let surahNumber = "";
              let surahNumberOfAyahs = "";
              let headerText = "";
              if (showSurahHeader) {
                if (paginationType === "page") {
                  currentSurahNumber = ayah.surah?.number;
                  surahName = ayah.surah?.name;
                  surahNumber = ayah.surah?.number;
                  surahNumberOfAyahs = ayah.surah?.numberOfAyahs;
                  headerText = `${surahName} - ${surahNumber} - عدد الآيات: ${surahNumberOfAyahs}`;
                } else {
                  currentSurahNumber = data?.number;
                  surahName = data?.name;
                  surahNumber = data?.number;
                  surahNumberOfAyahs = data?.numberOfAyahs;
                  if (displayStartAyah || displayEndAyah) {
                    const s = displayStartAyah || 1;
                    const e = displayEndAyah || surahNumberOfAyahs;
                    if (s !== 1 || e !== surahNumberOfAyahs) {
                      headerText = `${surahName} (${s} - ${e})`;
                    } else {
                      headerText = `${surahName} - ${surahNumber} - عدد الآيات: ${surahNumberOfAyahs}`;
                    }
                  } else {
                    headerText = `${surahName} - ${surahNumber} - عدد الآيات: ${surahNumberOfAyahs}`;
                  }
                }
              }

              return (
                <React.Fragment key={`ayah-${index}`}>
                  {showSurahHeader && (
                    <>
                      <div className="surah-name">
                        {headerText}
                      </div>
                      <div className='basmala'>
                        بِسْمِ ٱللَّهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ
                      </div>
                    </>
                  )}

                  <Verse ayah={ayah} currentAudio={currentAudio} currentVerseIndex={currentVerseIndex} hiddenVerses={hiddenVerses} index={index} splitVerseIntoWords={splitVerseIntoWords} surahNumber={surahNumber} togglePlayVerse={togglePlayVerse} toggleVerseVisibility={toggleVerseVisibility} isPlayingAll={isPlayingAll} />
                </React.Fragment>
              );
            }))}
        </div>
      </div>


    </div>
  );
}

export default App;